import styled from 'styled-components'

/**
 * A highly customized but equally extendable wrapper for uls.
 *
 * @summary Styles `<ul>`s in subheader that typically alternates
 * with `StyledNavWrapperWithDropDown` for breakpoint reasons.
 * Utilizise `#id` selector to override default styles declared elsewhere.
 * an `id` props has to be passed into a styled-component for it to register in the dom.
 *
 * @alias CategoriesModule
 * @see StyledNavWrapperWithDropDown
 * @example
  <StyledNavWithIdSelectorOverrides palette={palette}>
    <DeviceQueryToggle
      id="search"
      toggleSearchModal={toggleSearchModal}
      externalNavEnabled={externalNavEnabled}
      iconSrc={getVersionIcon(NAV_MODULE_ICON, version)}
    />
  </StyledNavWithIdSelectorOverrides>

  <CategoriesModule palette={palette}>
    <ul id="express-navigation">
      <ExpressNavigation
        query={query}
        language={language}
        onSvgLoad={onSvgLoad}
        showHomeLink={true && headerLogoLink}
        showExpressLink={true}
      />
    </ul>
  </CategoriesModule>

  <CategoriesModule palette={palette}>
    <DevicePicker
      id="device-picker"
      devices={devices}
      toggleDeviceModal={toggleDeviceModal}
      currentDevice={currentDevice}
      clearDevice={clearDevice}
  </CategoriesModule>
 */
export const StyledNavWithIdSelectorOverrides = styled.nav.attrs(() => ({
  className: 'animated fadeIn'
}))`
  --navStrokeHeight: ${props => props.palette('tabs.keylineHeight.sm')}; // ~6px

  ul {
    display: flex;
    padding: 0;
    flex-wrap: wrap;
    margin: 0;
  }

  li,
  span {
    color: var(--clr-nav-text);
    display: inline-block;
    vertical-align: middle;
    text-decoration: none;

    max-width: -webkit-fill-available;
    text-transform: ${props => props.palette('tabs.textTransform.sm')};
    font-weight: ${props => props.palette('tabs.fontWeight.sm')};
    letter-spacing: ${props => props.palette('tabs.letterSpacing.sm')};
  }
  /** Grab the DevicePicker and override its styles for v2 */
  #device-picker,
  #select,
  #clear {
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: normal;
    overflow: hidden;
    text-transform: ${props => props.palette('tabs.textTransform.md')};
    font-size: ${props => props.palette('tabs.fontSize.md')};
    font-weight: ${props => props.palette('tabs.fontWeight.md')};
    letter-spacing: ${props => props.palette('tabs.letterSpacing.md')};
    overflow: hidden;
    border-radius: 0;
    /** Necessary to remove bootstrap's button outlines */
    outline: none !important;
    background-color: ${props => props.palette('subheader.backgroundColor.md')};
    /** Make calcs to account for offsets and heights of pseudos so they responsively land.  */
    height: var(--navHeight);
  }

  #search {
    outline: none !important;
    margin-top: var(--navStrokeHeight, 6px);
    padding: 0;
  }

  /** Grab the RemoveDeviceText in DevicePicker and override its styles for versioned subheaders */
  #device-picker #clear > div,
  #clear > div {
    font-weight: ${props => props.palette('tabs.fontWeight.md')};
    letter-spacing: ${props => props.palette('tabs.letterSpacing.md')};
    text-overflow: ellipsis;
    text-transform: ${props => props.palette('tabs.textTransform.md')};
    overflow: hidden;
  }

  /** Grab the DeviceSelect button hover styles and override it for versioned subheaders */
  #clear:hover {
    color: #333;
    background-color: rgba(255, 255, 255, 0.25);
    border-color: #adadad;
  }
  span {
    display: block;
  }

  li {
    max-width: min-content;
    inline-size: fit-content;
    flex: 1 0 50%;
    text-align: center;
    position: relative;
    height: var(--navHeight);
  }

  a {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    text-decoration: none;
  }

  a:after {
    --line-height: ${props => props.palette('tabs.keylineHeight.sm')};
    content: '';
    display: block;

    position: absolute;
    -webkit-transform-origin: left;
    -ms-transform-origin: left;
    transform-origin: left;

    height: var(--line-height);
    width: 120px;
    background: ${props => props.palette('tabs.keylineBackgroundColor.sm')};
    border-radius: ${props => props.palette('tabs.keylineBorderRadius.sm')};

    top: calc(100% - var(--line-height));
    left: 8vw;

    -webkit-transform: scale(0, 1);
    -ms-transform: scale(0, 1);
    transform: scale(0, 1);
    -webkit-transition: -webkit-transform ease-in-out 250ms;
    -webkit-transition: transform ease-in-out 250ms;
    transition: transform ease-in-out 250ms;
  }

  a:hover::after,
  a:focus::after,
  a:active::after,
  a.nav-link.active-nav::after {
    transform: scale(1, 1);
  }

  /** Use id selectors to enforce style overrides to make non-reusables reusable as children. */
  #device-picker:hover svg,
  #device-picker:focus svg,
  #search:hover svg,
  #search:focus svg,
  #search:active svg,
  #select:hover svg,
  #select:focus svg,
  #select:active svg,
  #clear:hover svg,
  #clear:focus svg,
  #clear:active svg,
  a:hover svg,
  a:focus svg,
  a:active svg,
  a.active-nav svg {
    opacity: 1;
  }

  #search svg,
  #select svg,
  #clear svg,
  svg {
    width: ${props => props.palette('tabs.svgSize.sm')};
    height: ${props => props.palette('tabs.svgSize.sm')};
    fill: var(--white);
    margin: 5px 10px 5px 0px;
    display: inline-block;
    vertical-align: middle;
  }

  @media (max-width: 767px) {
    span {
      display: block;
    }

    li {
      max-width: 50%;
      text-align: center;
      position: relative;
    }
    a {
      justify-content: flex-start;
      margin-left: 20px;
      height: inherit;
    }

    #resources li {
      padding: 0 30px;
    }
    #resources li {
      margin-bottom: 10px;
      border-bottom: 1px solid;
    }
    #resources li:last-child,
    #resources li:nth-last-child(-n + 2):not(:nth-child(even)) {
      margin-bottom: 0px;
      border-bottom: none;
    }
    .device-selected li {
      padding: 0 30px;
    }
    .device-selected li:first-child {
      position: absolute;
      top: -53px;
      padding: 0;

      button {
        width: 75vw;
        padding: 6px 50px;
        flex-direction: row-reverse;
      }
    }

    #search {
      margin-top: 0;
      height: var(--navHeight);
    }

    #search svg,
    #select svg,
    #clear svg {
      margin: 5px 10px 5px 10px;
    }

    /**
     * Overrides for ExpressNavigation.
     * 'Support Center | Express Help' tabs on mobile.
     * These only concern versioned subheads.
     */
    #express-navigation {
      border-top: 1px solid var(--white);
      border-bottom: 1px solid var(--white);

      a:after {
        display: none;
      }

      li {
        flex-basis: 30%;

        button {
          margin-top: 0;
          font-size: 15px;
          height: 100%;
          justify-content: flex-end;
        }
      }
    }
  }

  @media (max-width: 576px) {
    /** Device select text styles  */
    #select,
    #clear {
      text-transform: ${props => props.palette('tabs.textTransform.sm')};
      font-size: ${props => props.palette('tabs.fontSize.sm')};
      font-weight: ${props => props.palette('tabs.fontWeight.sm')};
      letter-spacing: ${props => props.palette('tabs.letterSpacing.sm')};
    }
    a:after {
      left: 10vw;
    }
  }

  @media (max-width: 380px) {
    /* Break long text such as "Support Center" on tiny screens */
    #express-navigation span {
      inline-size: 50%;
      white-space: normal;
      line-height: normal;
      text-align: start;
    }
  }
`

/** Name styled-components so they don't trace in ReactDevTools as anonymous `styled.div`s  */
/** Feel free to change in dev */
StyledNavWithIdSelectorOverrides.displayName =
  'StyledNavWithIdSelectorOverrides'



// WEBPACK FOOTER //
// ./src/components/SubHeader/styled/StyledNavWithIdSelectorOverrides.js